import Model from './model.js';
import Companies from 'newCollections/companies.js';
import Employee from './employee.js';
import Subscription from './subscription.js';
import SyncError from '../errors/sync-error.js';
import axios from 'axios';
import Log from 'lib/log';
import config from 'config';

const RESET_URL = config.apiUrl + `/AppUsers/reset`;
const USER_URL = config.apiUrl + `/AppUsers`;
const INVITE_MPP_ADMIN_URL = config.apiUrl + `/AppUsers/inviteMPPAdmin`;
const INVITE_MPP_MANAGER_URL = config.apiUrl + `/AppUsers/inviteMPPManager`;
const INVITE_MPP_CUSTOMER_URL = config.apiUrl + `/AppUsers/inviteMPPCustomer`;
const INVITE_LEGACY_USER_URL = config.apiUrl + `/AppUsers/inviteLegacyUser`;
const MPP_PERMISSIONS_URL = config.apiUrl + `/AppUsers/setMppManagerPermissions`;
const log = Log('model/user');

export default class User extends Model {
    urlRoot() { return '/AppUsers'; }

    defaults() {
        return {
            role: 'employer'
        };
    }

    constraints() {
        const loggedIn = this.isLoggedIn();
        return {
            name: {
                presence:  {message: '^Name is required'},
                length: {
                    minimum: 2,
                    message: '^Name is too short'
                }
            },
            username: {
                presence: {message: '^Username email is required'},
                length: {
                    minimum: 4,
                    message: '^Username email must be at least 4 characters'
                }
            },
            email: {
                email:    '^Email is required',
                presence: {message: '^Email is not valid'}
            },
            password: function() {
                //If you are logged in, no need to validate password presence.
                if (!loggedIn) {
                    return {
                        presence: {
                            message: '^Password must be at least 8 characters'
                        },
                        length: {
                            minimum: 8,
                            message: '^Password must be at least 8 characters'
                        }
                    };
                }
            },
            contactPhone: function(value, attrs) {
                if (
                    !loggedIn && attrs.role === 'employer' &&
                    !attrs.isMPPCustomer
                ) {
                    return {
                        presence: {message: '^Contact phone is required'},
                        format: {
                            pattern: '[0-9\\(\\)\\+\\- ]+',
                            message: '^The contact phone must be a valid ' +
                                'phone number'
                        }
                    };
                }

                return null;
            }
        };
    }

    initialize() {
        const r = super.initialize(...arguments);
        this.companies = new Companies(null, {user: this});
        this.subscription = new Subscription(null, {user: this});

        this.on('change:email', this.syncEmailAndUsername('email'), this);
        this.on('change:username', this.syncEmailAndUsername('username'), this);
        this.on('destroy', () => this.off(null, null, this), this);

        return r;
    }

    syncEmailAndUsername(sourceField) {
        const targetField =
            sourceField === 'email' ? 'username' :
                sourceField === 'username' ? 'email' :
                    null;

        if (targetField === null) {
            return () => {}; //noop
        }
        return () => {
            this.set(targetField, this.get(sourceField), {
                validate: false,
                silent: true
            });
            return this;
        };
    }

    login(credentials = {email: null, password: null}) {
        const url = this.baseUrl() + '/login';
        if (this.isLoggedIn()) {
            return Promise.reject(new Error('User is already authenticated'));
        }

        // TODO use axios
        return Promise.resolve(Backbone.$.ajax({
            method: 'POST',
            url: url,
            data: credentials
        }))
            .then(this.setAccessToken)
            .then(() => {
                this.set('id', this.getAccessToken().userId);
                return this.fetch();
            })
            .then(() => this.trigger('loggedIn'))
            .catch(err => {
                if (!(err instanceof SyncError)) {
                    return Promise.reject(new SyncError(
                        this,
                        err.responseJSON || err.responseText
                    ));
                }
                return Promise.reject(err);
            });
    }

    logout() {
        return axios({
            method: 'post',
            url: `${this.baseUrl()}/logout`,
            headers: {'Authorization': this.getAccessToken().id}
        }).then(() => {
            window.localStorage.removeItem('access-token');
            this.trigger('loggedOut');
        }).catch(err => {
            console.error(err);
            throw err;
        });
    }

    setAccessToken(token) {
        window.localStorage.setItem('access-token', JSON.stringify(token));
    }

    getAccessToken() {
        return JSON.parse(window.localStorage.getItem('access-token'));
    }

    removeAccessToken() {
        window.localStorage.removeItem('access-token');
    }

    isLoggedIn() {
        return this.id && this.getAccessToken();
    }

    isRequirePasswordChange() {
        return this.isLoggedIn() && !this.get('passwordChangedAt') &&
            (this.isEmployer() || this.isAdmin()) &&
            /jibble\.io/.test(this.get('email'));
    }

    changePassword(oldPassword, newPassword) {
        return axios.post(
            `${this.baseUrl()}/change-password`,
            {
                oldPassword: oldPassword,
                newPassword: newPassword
            },
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch())
            .catch(err => {
                throw err.response.data.error;
            });
    }

    sendCustomerSupportEmail(questionSubject, desc) {
        return axios.post(
            `${this.baseUrl()}/customerSupport`,
            {
                subject: questionSubject,
                desc,
            },
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).catch(err => {
            throw err.response.data.error;
        });
    }

    remove() {
        return axios.delete(this.finalUrl(), {
            headers: {'Authorization': app.user.getAccessToken().id}
        });
    }

    fetchCompany() {
        return this.companies.fetch({validate: false})
            .then(() => this.companies.at(0).fetch())
            .then(() => this.companies.at(0));
    }

    fetchEmployee() {
        return new Employee({user: this}).fetch({validate: false});
    }

    fetchSubscription() {
        return this.subscription.fetch()
            .catch(err => {
                log.debug('Silently ignoring subscription failure:', err);
            });
    }

    getSubscription() {
        return this.subscription;
    }

    setRole(role) {
        return axios.post(
            this.finalUrl() + '/setRole',
            {role: role},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(() => this.fetch())
            .catch(err => {
                throw err.response.data.error;
            });
    }

    isAdmin() {
        return [`admin_manager`, `super_admin`].includes(this.get(`role`));
    }

    isSuperAdmin() {
        return this.get(`role`) === `super_admin`;
    }

    isEmployee() {
        return this.get(`role`) === `employee`;
    }

    isEmployer() {
        return this.get(`role`) === `employer`;
    }

    removeLogo() {
        return axios.delete(`${this.finalUrl()}/logo`, {
            headers: {'Authorization': app.user.getAccessToken().id}
        });
    }

    uploadLogo(file = null) {
        if (!file) {
            return Promise.resolve();
        }

        // Size limit: 200 kb
        if (file && file.size && file.size > 1024 * 200) {
            return Promise.reject(`The maximum allowed size is 200kb`);
        }

        if (!/^image\/(png|jpeg)$/.test(file.type)) {
            return Promise.reject(`Only PNG or JPEG images are allowed`);
        }

        const extension = file.name.split('.').pop();
        const data = new FormData();
        data.append(`file`, file, `mpp-logo.${extension}`);

        return axios.post(
            `${this.finalUrl()}/logo`,
            data,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        );
    }

    getLogoUrl(id = null) {
        const url = id ? `${USER_URL}/${id}` : this.finalUrl();
        return `${url}/logo?` +
            `access_token=${app.user.getAccessToken().id}&` +
            `_b=${Math.floor(Math.random() * 1000000)}`;
    }

    static signUp({
        name, email, password, token, contactPhone, disclosedEmployeeCount
    }) {
        return axios({
            method: 'post',
            url: `${config.apiUrl}/AppUsers/signup/${token || ''}`,
            data: {
                name, email, password, contactPhone,
                disclosedEmployeeCount
            }
        }).then(res => res.data);
    }

    static inviteMppAdmin(name, email) {
        return axios.post(INVITE_MPP_ADMIN_URL, {
            name,
            email
        },
        {headers: {'Authorization': app.user.getAccessToken().id}}
        );
    }

    static inviteMppManager(name, email, companies) {
        return axios.post(INVITE_MPP_MANAGER_URL, {
            name,
            email,
            companies
        },
        {headers: {'Authorization': app.user.getAccessToken().id}}
        );
    }

    static inviteLegacyUser(name, email) {
        return axios.post(INVITE_LEGACY_USER_URL, {
            name,
            email
        },
        {headers: {'Authorization': app.user.getAccessToken().id}}
        );
    }

    static inviteMppCustomer(name, email, mppAdminId) {
        return axios.post(
            INVITE_MPP_CUSTOMER_URL,
            {name, email, mppAdminId},
            {headers: {'Authorization': app.user.getAccessToken().id}}
        );
    }

    static setMppManagerPermissions(manager, companies) {
        return axios.post(MPP_PERMISSIONS_URL, {
            manager,
            companies
        },
        {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(result => {
            return result.data;
        });
    }

    static resetPassword(email) {
        return axios.post(RESET_URL, {email});
    }
}



// WEBPACK FOOTER //
// ./app/new-models/user.js